import React, { useEffect, useRef, useState } from "react";
import { notify } from "../Notification";
import { formatListTime, formatTime } from "../../hooks/useClock";
import { CONTACTS, MY_CARD, fullName } from "../../data/contacts";
import { contact } from "../../data/search";
import { THREADS } from "../../data/messages";
import Avatar from "./Avatar";
import { AppShell, BackButton, Decor, Screen, SearchField, useKeyboardInset, useNavStack } from "./ios";
import { ReactComponent as FilterIcon } from "../../assets/svgs/sf-symbols/phone/line.3.horizontal.decrease.svg";
import { ReactComponent as ComposeIcon } from "../../assets/svgs/sf-symbols/notes/square.and.pencil.svg";
import { ReactComponent as ChevronRight } from "../../assets/svgs/sf-symbols/ios/chevron.right.svg";
import { ReactComponent as PlusIcon } from "../../assets/svgs/sf-symbols/phone/plus.svg";
import { ReactComponent as VideoIcon } from "../../assets/svgs/sf-symbols/phone/video.fill.svg";
import { ReactComponent as MicIcon } from "../../assets/svgs/sf-symbols/ios/microphone.fill.svg";
import { ReactComponent as SendIcon } from "../../assets/svgs/sf-symbols/messages/arrow.up.svg";

const PEOPLE = { me: MY_CARD, ...Object.fromEntries(CONTACTS.map((c) => [c.id, c])) };
const nameOf = (thread) => (thread.who === "me" ? MY_CARD.first : fullName(PEOPLE[thread.who]));
const MAIL_SUBJECT = "Hello from onlysly.com";
const REPLY =
  "Thanks! Your email app should have opened with your message. Send it from there and I'll get back to you.";

// The time above a conversation: "Today 9:41 PM", "Yesterday 9:41 PM", "Friday 9:41 PM", or a date.
const stamp = (date, now) => {
  const day = formatListTime(date, now);
  return { day: day === formatTime(date) ? "Today" : day, time: formatTime(date) };
};

const ListScreen = ({ threads, now, open, compose }) => {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = threads.filter(
    (t) => !q || `${nameOf(t)} ${t.messages.map((m) => m.text).join(" ")}`.toLowerCase().includes(q)
  );

  return (
    <Screen
      title="Messages"
      left={<Decor className="ios-pill">Edit</Decor>}
      right={
        <Decor>
          <FilterIcon className="ph-sym-filter" />
        </Decor>
      }
      bottom={
        <>
          <SearchField
            className="ios-search-field glass glass--light"
            value={query}
            onChange={setQuery}
            label="Search Messages"
          />
          <button type="button" className="ios-circle glass glass--light" onClick={compose} aria-label="New Message">
            <ComposeIcon className="ios-sym-compose" />
          </button>
        </>
      }
    >
      {shown.map((t) => {
        const last = t.messages[t.messages.length - 1];
        return (
          <button type="button" key={t.id} className="msg-row" onClick={() => open(t.id)}>
            {t.unread && <span className="msg-dot" aria-label="Unread" />}
            <Avatar person={PEOPLE[t.who]} size={46} />
            <span className="msg-row-text">
              <span className="msg-row-top">
                <span className="msg-row-name">{nameOf(t)}</span>
                <span className="msg-row-time">{formatListTime(t.date, now)}</span>
                <ChevronRight className="msg-chevron" aria-hidden="true" />
              </span>
              <span className="msg-row-preview">{last.text}</span>
            </span>
          </button>
        );
      })}
      {!shown.length && <p className="ios-empty">No Results</p>}
    </Screen>
  );
};

// One conversation: their messages on the left in gray, yours on the right in blue, each run of
// bubbles ending in a tail, and the bar to write a message at the bottom.
const ThreadScreen = ({ thread, now, back, send }) => {
  const [draft, setDraft] = useState("");
  const end = useRef(null);
  const { day, time } = stamp(thread.started, now);

  // Keep the newest message in view as messages arrive.
  useEffect(() => {
    if (end.current) end.current.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [thread.messages.length]);

  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    send(thread.id, text);
    setDraft("");
  };

  return (
    <Screen
      left={<BackButton onClick={back} />}
      center={
        <span className="msg-header">
          <Avatar person={PEOPLE[thread.who]} size={40} />
          <span className="msg-header-name glass glass--light">
            {nameOf(thread)}
            <ChevronRight className="msg-header-chevron" aria-hidden="true" />
          </span>
        </span>
      }
      right={
        <Decor>
          <VideoIcon className="msg-sym-video" />
        </Decor>
      }
      bottom={
        <form className="msg-compose" onSubmit={submit}>
          <Decor>
            <PlusIcon className="ph-sym-plus" />
          </Decor>
          <span className="msg-field glass glass--light">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={thread.who === "me" ? "iMessage" : "Text Message"}
              aria-label="Message"
              enterKeyHint="send"
              autoComplete="off"
            />
            {draft.trim() ? (
              <button type="submit" className="msg-send" aria-label="Send">
                <SendIcon className="msg-sym-send" />
              </button>
            ) : (
              <MicIcon className="ios-sym-mic" aria-hidden="true" />
            )}
          </span>
        </form>
      }
    >
      <div className="msg-thread">
        <p className="msg-stamp">
          <b>{day}</b> {time}
        </p>
        {thread.messages.map((m, i) => {
          const next = thread.messages[i + 1];
          const tail = !next || next.from !== m.from;
          return (
            <React.Fragment key={i}>
              <div className={`msg-bubble from-${m.from} ${tail ? "has-tail" : ""}`}>{m.text}</div>
              {m.status && <p className={`msg-status ${m.status === "Not Delivered" ? "is-failed" : ""}`}>{m.status}</p>}
            </React.Fragment>
          );
        })}
        <div ref={end} />
      </div>
    </Screen>
  );
};

// Messages (iOS 26), like Sylvester's: the conversation list with unread dots, and search and
// compose at the bottom. The made-up conversations can't send ("Not Delivered"); the one with
// Sylvester opens what you write as an email to him, and he answers.
const MessagesApp = ({ now, onClose }) => {
  const [threads, setThreads] = useState(() =>
    [...THREADS]
      .sort((a, b) => a.ago - b.ago)
      .map((t) => {
        const date = new Date(now.getTime() - t.ago * 60000);
        return { ...t, date, started: date };
      })
  );
  const nav = useNavStack([{ screen: "list" }]);
  const keyboard = useKeyboardInset(nav.stack.length > 1);
  const replied = useRef(false);

  const open = (id) => {
    setThreads((ts) => ts.map((t) => (t.id === id ? { ...t, unread: false } : t)));
    nav.push({ screen: "thread", id });
  };

  const add = (id, message) =>
    setThreads((ts) => {
      const updated = ts.map((t) => (t.id === id ? { ...t, date: new Date(), messages: [...t.messages, message] } : t));
      // A conversation with a new message moves to the top of the list.
      return [...updated].sort((a, b) => b.date - a.date);
    });

  const send = (id, text) => {
    if (id !== "me") {
      add(id, { from: "me", text, status: "Not Delivered" });
      notify("Messages don't go out from this replica. Message me in the Sylvester conversation instead.");
      return;
    }
    add(id, { from: "me", text, status: "Delivered" });
    const href = `${contact("Email").href}?subject=${encodeURIComponent(MAIL_SUBJECT)}&body=${encodeURIComponent(text)}`;
    window.open(href, "_blank", "noopener");
    if (!replied.current) {
      replied.current = true;
      setTimeout(() => add(id, { from: "them", text: REPLY }), 1200);
    }
  };

  return (
    <AppShell label="Messages" origin="39% 92%" onClose={onClose}>
      {nav.pages.map((entry) => (
        <div
          key={entry.key}
          {...nav.pageProps(entry, "ios-page--grouped msg-page")}
          style={entry.screen === "thread" ? { "--keyboard": `${keyboard}px` } : undefined}
        >
          {entry.screen === "thread" ? (
            <ThreadScreen thread={threads.find((t) => t.id === entry.id)} now={now} back={nav.back} send={send} />
          ) : (
            <ListScreen threads={threads} now={now} open={open} compose={() => open("me")} />
          )}
        </div>
      ))}
    </AppShell>
  );
};

export default MessagesApp;
